// Sentral konverterings-sporing. Fyrer ETT GA4-event + Meta Pixel «Lead» for hver
// lead/konvertering, slik at «Viktige hendelser» i GA4 og custom audiences i Meta
// blir konsistente på tvers av alle skjemaer.
//
// VIKTIG: GA4 viser ikke et event under «Viktige hendelser» før det er markert som
// nøkkelhendelse i GA4-admin (Admin → Hendelser → «Marker som nøkkelhendelse»).
// Event-navnene som brukes i dag: waitlist_signup, generate_lead (matplan/ukeplan),
// recipe_email_signup, ebook_download. De tre første er allerede merket som
// nøkkelhendelser i GA4 (property 529241624).

import { hentSamtykke } from "./samtykke";

type LeadParams = Record<string, unknown>;

function genEventId(): string {
  try {
    const c = (window as unknown as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID();
  } catch { /* ignore */ }
  return "lead_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function trackLead(event: string, params: LeadParams = {}): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  };
  // Felles event_id deduplikerer nettleser-pixel og server-side CAPI hos Meta.
  const eventId = genEventId();

  // GA4 — nøkkelhendelse (grunnlag for Google Ads-konvertering + audiences)
  w.gtag?.("event", event, params);
  // Meta Pixel — standard Lead-event, med eventID for dedup mot CAPI
  w.fbq?.("track", "Lead", { content_name: event, content_category: "lead", ...params }, { eventID: eventId });

  // Server-side Conversions API (fire-and-forget) — gjenvinner leads pixelen mister
  // til ad-blockere/ITP. E-post (hvis sendt) hashes server-side, aldri i custom_data.
  // Bare med samtykke til markedsføring: uten det skal Meta ikke få leadet, verken
  // fra nettleseren (pixelen er da ikke lastet) eller fra serveren.
  if (!hentSamtykke()?.marketing) return;
  try {
    const email = typeof params.email === "string" ? params.email : undefined;
    const custom: Record<string, unknown> = { content_name: event, content_category: "lead" };
    for (const [k, v] of Object.entries(params)) if (k !== "email") custom[k] = v;
    const blob = JSON.stringify({
      event_name: "Lead",
      event_id: eventId,
      event_source_url: window.location.href,
      email,
      custom_data: custom,
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/meta-capi", new Blob([blob], { type: "application/json" }));
    } else {
      void fetch("/api/meta-capi", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: blob,
        keepalive: true,
      });
    }
  } catch { /* sporing skal aldri kaste */ }
}

// Butikk-trakt-sporing (lenkeklikk + checkout-start). Egne Meta-standardhendelser
// (ViewContent/InitiateCheckout) i stedet for trackLead sitt "Lead" — disse
// klikkene er ikke leads og skal ikke forurense Lead-konverteringen Meta
// optimaliserer annonsene mot. Ingen server-side CAPI: ingen e-post å hashe,
// og disse hendelsene er kun for trakt-synlighet, ikke annonseoptimalisering.
export function trackProductEvent(
  stage: "select_content" | "begin_checkout",
  params: LeadParams = {},
): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  };
  const metaEvent = stage === "begin_checkout" ? "InitiateCheckout" : "ViewContent";
  w.gtag?.("event", stage, params);
  w.fbq?.("track", metaEvent, params);
}
